import { Netmask } from 'netmask';
import { parseDomain, ParseResultType } from 'parse-domain';
import { z } from 'zod';

export const EMPTY_ERR = 'Requires public IP address or hostname';
export const IPV6_INVALID = 'IPv6 is not supported, please use an IPv4 address.';
export const INVALID_ADDR_ERR = 'Invalid IPv4 address or hostname';
export const UNSPECIFIED_ADDR_ERR = 'Cannot use unspecified IPv4 address';
export const LOOPBACK_OR_PRIVATE_ADDR_ERR = 'Cannot use loopback or private IPv4 address or hostname';
export const NEBULA_NETWORK_ERR = 'Provide a public internet IP address, not an address in your overlay network';
export const NO_TRAILING_DOT = 'Hostname must not have a trailing dot';

function validatePublicIPOrHostname(ipOrHostname: string, cidrs: readonly string[]): string | true {
  if (!ipOrHostname) {
    return EMPTY_ERR;
  }

  const parseResult = parseDomain(ipOrHostname);

  switch (parseResult.type) {
    case ParseResultType.NotListed:
    case ParseResultType.Listed: {
      if (parseResult.hostname.endsWith('.')) {
        return NO_TRAILING_DOT;
      }
      return true;
    }
    case ParseResultType.Reserved: {
      return LOOPBACK_OR_PRIVATE_ADDR_ERR;
    }
    case ParseResultType.Ip: {
      const { ipVersion } = parseResult;

      if (ipVersion === 6) {
        return IPV6_INVALID;
      }

      return validatePublicIP(ipOrHostname, cidrs);
    }
    case ParseResultType.Invalid: {
      const { errors } = parseResult;
      return errors[0]?.message ?? INVALID_ADDR_ERR;
    }
    default:
      throw new Error(INVALID_ADDR_ERR);
  }
}

function validatePublicIP(ip: string, cidrs: readonly string[]) {
  if (ip === '0.0.0.0') {
    return UNSPECIFIED_ADDR_ERR;
  }

  if (ip === '127.0.0.1') {
    return LOOPBACK_OR_PRIVATE_ADDR_ERR;
  }

  // No overlay CIDRs (e.g. v6-only overlay) means no v4 collision is possible.
  if (cidrs.length === 0) {
    return true;
  }

  const netmasks = cidrs.map((cidr) => new Netmask(cidr));
  // Check if the IP is outside at least *one* nebula network.
  const hasOneValidNetwork = netmasks.some((netmask) => !netmask.contains(ip));

  if (!hasOneValidNetwork) {
    return NEBULA_NETWORK_ERR;
  }

  return true;
}

export function makePublicIPOrHostnameSchema(networkCIDRs: readonly string[]) {
  return z
    .string({
      errorMap: (issue, ctx) => {
        if (issue.code === 'invalid_type' && issue.received === 'undefined') {
          return { message: EMPTY_ERR };
        }
        return { message: ctx.defaultError };
      },
    })
    .superRefine((arg, ctx) => {
      const result = validatePublicIPOrHostname(arg, networkCIDRs);

      if (typeof result === 'string') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: result,
          fatal: true,
        });

        return z.NEVER;
      }

      return z.NEVER;
    });
}
